import type { ExpressionSpecification } from 'mapbox-gl';
import {
  buildTrailheadCountTextField,
  buildTrailheadIconCollapsedOffset,
  buildTrailheadIconImage,
  buildTrailheadIconOffset,
  buildTrailheadNameTextOffset,
  buildTrailheadSelectedIconImage,
  buildTrailheadTextOffset
} from '../../utils/buildTrailheadIconExpressions';
import { getTrailheadPinStyle } from '../../utils/trailheadPinVariant';
import TrailheadPinLayers, { type TrailheadPinLayerProps } from '../TrailheadPinLayers/TrailheadPinLayers';

type TrailheadVectorPinLayerProps = TrailheadPinLayerProps & {
  trailCountExpression: ExpressionSpecification;
};

/**
 * The circle pins: a circle, with a count banner for trailheads with more than one trail. A hovered pin
 * swaps to the selected pin.
 */
const TrailheadVectorPinLayer = ({ isHoveredExpression, pinVariant, trailCountExpression, ...layerProps }: TrailheadVectorPinLayerProps) => {
  const pinStyle = getTrailheadPinStyle(pinVariant);
  const variantExpressions = {
    isClosedExpression: layerProps.isClosedExpression,
    isCompletedExpression: layerProps.isCompletedExpression,
    isSavedExpression: layerProps.isSavedExpression
  };

  return (
    <TrailheadPinLayers
      {...layerProps}
      collapsedActiveLayout={{
        'icon-image': buildTrailheadSelectedIconImage(variantExpressions, pinStyle),
        'icon-offset': buildTrailheadIconCollapsedOffset(isHoveredExpression)
      }}
      expandedLayout={{
        'icon-image': buildTrailheadIconImage(variantExpressions, isHoveredExpression, trailCountExpression, pinStyle),
        'icon-offset': buildTrailheadIconOffset(isHoveredExpression),
        // The count owns an expanded pin's `text-field` and its offset, so the names are on layers of their own
        'text-field': buildTrailheadCountTextField(isHoveredExpression, trailCountExpression),
        'text-offset': buildTrailheadTextOffset(variantExpressions, isHoveredExpression)
      }}
      isActiveExpression={isHoveredExpression}
      nameTextOffset={buildTrailheadNameTextOffset(trailCountExpression)}
      pinVariant={pinVariant}
    />
  );
};

export default TrailheadVectorPinLayer;
